import { useEffect, useState } from "react";
import { CheckCircle2, Circle, Lock } from "lucide-react";
import SectionHeader from "../components/common/SectionHeader";
import { app } from "../application";
import { isCommercialContext } from "../config/environment";
import { DEFAULT_ORGANIZATION_ID, DEFAULT_ORGANIZATION_NAME } from "../config/vertical";

const STORAGE_KEY = "co_delivery_checklist_v1";

/**
 * Checklist de entrega — herramienta interna de Fernando, nunca visible
 * para Abel (gateada por !isCommercialContext(), igual que la sección
 * técnica de Settings.jsx). No reemplaza el criterio humano: los ítems que
 * requieren juicio o verificar OTRA organización se marcan a mano
 * (persistidos en este navegador); solo lo que esta build puede confirmar
 * por sí misma se marca automáticamente.
 */
const MANUAL_ITEMS = [
  { key: "branding", label: "Branding correcto (nombre, tono, sin restos de Emergent/GHL visibles)" },
  { key: "acceso_probado", label: "Acceso probado de extremo a extremo desde otro navegador/dispositivo" },
  { key: "enlace_generado", label: "Enlace de acceso (invitación) generado y guardado de forma segura" },
  { key: "organizacion_vacia", label: "Confirmado manualmente: la organización de Abel está vacía (sin datos de laboratorio)" },
  { key: "whatsapp_correcto", label: "Número de WhatsApp conectado es el correcto para Abel" },
  { key: "importacion_correcta", label: "Plantilla de importación probada con un archivo de ejemplo" },
  { key: "secretos_regenerados", label: "Token de acceso y secreto de webhook regenerados para esta entrega" },
  { key: "experiencia_aprobada", label: "Recorrido completo aprobado por Fernando (Fase 1-2 del recorrido)" },
  { key: "listo_para_invitar", label: "Listo para enviar la invitación definitiva" },
];

export default function DeliveryChecklist() {
  const [manual, setManual] = useState({});
  const [auto, setAuto] = useState(null);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
      setManual(saved);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    (async () => {
      const team = await app.team.list();
      setAuto({
        organizacionConfigurada: DEFAULT_ORGANIZATION_ID !== "default-organization",
        httpsListo: window.location.protocol === "https:",
        equipoInicializado: (team || []).length >= 1,
      });
    })();
  }, []);

  const toggle = (key) => {
    setManual((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  };

  if (isCommercialContext()) {
    return (
      <div className="ambient-bg min-h-full px-8 py-16 flex flex-col items-center text-center text-zinc-500">
        <Lock size={20} className="mb-3" />
        Esta pantalla no está disponible en este contexto.
      </div>
    );
  }

  const crossSellEnabledHere = process.env.REACT_APP_CROSS_SELL_ENABLED === "true";
  const AUTO_ITEMS = auto
    ? [
        {
          label: "Organización configurada (no es el default genérico)",
          ok: auto.organizacionConfigurada,
          detail: DEFAULT_ORGANIZATION_NAME,
        },
        {
          label: "Venta Cruzada desactivada en esta build",
          ok: !crossSellEnabledHere,
          detail: crossSellEnabledHere ? "ACTIVADA — no debe estarlo para Abel" : "Desactivada",
        },
        {
          label: "Acceso servido por HTTPS",
          ok: auto.httpsListo,
          detail: window.location.protocol,
        },
        {
          label: "Equipo/operador inicializado",
          ok: auto.equipoInicializado,
          detail: `${auto.equipoInicializado ? "Sí" : "No"}`,
        },
      ]
    : [];

  const totalItems = AUTO_ITEMS.length + MANUAL_ITEMS.length;
  const doneItems = AUTO_ITEMS.filter((i) => i.ok).length + Object.values(manual).filter(Boolean).length;

  return (
    <div className="ambient-bg min-h-full px-8 py-8">
      <SectionHeader
        overline="Solo Fernando · no visible para Abel"
        title="Checklist de entrega"
        subtitle={`${doneItems} de ${totalItems} confirmados. Esta pantalla vive únicamente en el entorno de laboratorio.`}
      />

      <div className="mt-8 max-w-2xl space-y-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-3">
            Verificado automáticamente por esta build
          </div>
          <div className="space-y-2">
            {AUTO_ITEMS.map((item, i) => (
              <div key={i} className="flex items-center gap-3 border border-[#17223a] bg-[#0d1321] px-4 py-3">
                {item.ok ? (
                  <CheckCircle2 size={16} className="text-emerald-400 shrink-0" />
                ) : (
                  <Circle size={16} className="text-red-400 shrink-0" />
                )}
                <div className="flex-1 text-sm text-zinc-200">{item.label}</div>
                <div className="text-xs text-zinc-500 font-mono">{item.detail}</div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-3">
            Confirmación manual (tu criterio)
          </div>
          <div className="space-y-2">
            {MANUAL_ITEMS.map((item) => (
              <button
                key={item.key}
                onClick={() => toggle(item.key)}
                className="w-full flex items-center gap-3 border border-[#17223a] bg-[#0d1321] px-4 py-3 text-left hover:border-amber-500/40 transition-colors"
              >
                {manual[item.key] ? (
                  <CheckCircle2 size={16} className="text-emerald-400 shrink-0" />
                ) : (
                  <Circle size={16} className="text-zinc-600 shrink-0" />
                )}
                <div className="flex-1 text-sm text-zinc-200">{item.label}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
